import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { FileText, Calendar, Gavel, AlertTriangle } from 'lucide-react';
import Modal from '../Modal';
import {
  LeadDto,
  ClientSummaryForLeadDto,
  ClientSummaryAppointmentDto,
  ClientSummaryCommitteeDto,
  leadsApi,
  handleApiError,
  formatDate,
} from '../../services/leadsService';

interface ClientDataViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  lead: LeadDto;
}

// סדר קבוע לטאבים (תואם ל-enums בבקאנד)
const APPOINTMENT_STATUS_ORDER: { key: number; label: string }[] = [
  { key: 5, label: 'צריך תיאום' },  // NeedScheduling
  { key: 1, label: 'מתוזמן' },       // Scheduled
  { key: 2, label: 'אושר' },         // Confirmed
  { key: 3, label: 'הושלם' },        // Completed
  { key: 4, label: 'בוטל' },         // Cancelled
];

const COMMITTEE_STATUS_ORDER: { key: number; label: string }[] = [
  { key: 1, label: 'ממתין להחלטה' },  // Pending
  { key: 2, label: 'אושר לצמיתות' },  // ApprovedPermanent
  { key: 4, label: 'אושר לתקופה' },   // ApprovedForPeriod
  { key: 3, label: 'נדחה' },          // Rejected
];

type TabKey = number | 'all';

const Section = styled.div`
  & + & {
    margin-top: 1.25rem;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.75rem;

  svg {
    color: var(--primary-color);
  }
`;

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 8px;
  border-radius: 12px;
  background: var(--primary-light, #e6f0fd);
  color: var(--primary-color);
  font-size: 13px;
  font-weight: 600;
`;

const TabsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 0.75rem;
`;

const TabButton = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.3rem 0.7rem;
  border-radius: var(--border-radius-sm);
  border: 1px solid ${props => (props.$active ? 'var(--primary-color)' : 'var(--border-color)')};
  background: ${props => (props.$active ? 'var(--primary-color)' : 'var(--surface-color)')};
  color: ${props => (props.$active ? '#fff' : 'var(--text-secondary, #676879)')};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: var(--primary-color);
  }
`;

const TabCount = styled.span<{ $active: boolean }>`
  font-size: 12px;
  font-weight: 700;
  color: ${props => (props.$active ? '#fff' : 'var(--text-tertiary, #9699a6)')};
`;

const ItemList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ItemCard = styled.div`
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  padding: 0.6rem 0.75rem;
  background: var(--surface-color);
`;

const ItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const ItemTitle = styled.span`
  font-weight: 600;
  color: var(--text-primary);
  font-size: 14px;
`;

const ItemMeta = styled.span`
  color: var(--text-secondary, #676879);
  font-size: 13px;
`;

const DecisionTag = styled.span<{ $muted?: boolean }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  background: ${props => (props.$muted ? 'var(--hover-background, #f5f6f8)' : 'var(--primary-light, #e6f0fd)')};
  color: ${props => (props.$muted ? 'var(--text-tertiary, #9699a6)' : 'var(--primary-color)')};
  font-size: 12px;
  font-weight: 600;
`;

const DecisionNotes = styled.p`
  margin: 0.4rem 0 0;
  font-size: 13px;
  color: var(--text-secondary, #676879);
  line-height: 1.5;
`;

const EmptyText = styled.div`
  color: var(--text-tertiary, #9699a6);
  font-size: 13px;
  padding: 0.5rem 0;
`;

const StateBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 2rem 1rem;
  color: var(--text-secondary, #676879);
  text-align: center;

  svg {
    color: var(--error-color, #fb275d);
  }
`;

const formatDateTime = (value?: string): string => {
  if (!value) return 'ללא תאריך';
  const date = new Date(value);
  if (isNaN(date.getTime())) return formatDate(value);
  const time = date.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
  return `${formatDate(value)} בשעה ${time}`;
};

// בניית רשימת טאבים: "הכל" + כל סטטוס שקיימת בו לפחות רשומה אחת
function buildTabs(
  order: { key: number; label: string }[],
  getStatus: (item: { status?: number; decisionStatus?: number }) => number,
  items: { status?: number; decisionStatus?: number }[]
): { key: TabKey; label: string; count: number }[] {
  const tabs: { key: TabKey; label: string; count: number }[] = [
    { key: 'all', label: 'הכל', count: items.length },
  ];
  for (const s of order) {
    const count = items.filter(i => getStatus(i) === s.key).length;
    if (count > 0) tabs.push({ key: s.key, label: s.label, count });
  }
  return tabs;
}

const TabBar: React.FC<{
  tabs: { key: TabKey; label: string; count: number }[];
  active: TabKey;
  onChange: (key: TabKey) => void;
}> = ({ tabs, active, onChange }) => (
  <TabsRow>
    {tabs.map(t => (
      <TabButton key={String(t.key)} $active={active === t.key} onClick={() => onChange(t.key)} type="button">
        {t.label}
        <TabCount $active={active === t.key}>{t.count}</TabCount>
      </TabButton>
    ))}
  </TabsRow>
);

const ClientDataViewerModal: React.FC<ClientDataViewerModalProps> = ({ isOpen, onClose, lead }) => {
  const [summary, setSummary] = useState<ClientSummaryForLeadDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [apptTab, setApptTab] = useState<TabKey>('all');
  const [committeeTab, setCommitteeTab] = useState<TabKey>('all');

  const loadSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    setApptTab('all');
    setCommitteeTab('all');
    try {
      const data = await leadsApi.getClientSummary(lead.id);
      setSummary(data);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  }, [lead.id]);

  useEffect(() => {
    if (isOpen) {
      loadSummary();
    }
  }, [isOpen, loadSummary]);

  const appointments = useMemo(() => summary?.appointments ?? [], [summary]);
  const committees = useMemo(() => summary?.committees ?? [], [summary]);

  const apptTabs = useMemo(
    () => buildTabs(APPOINTMENT_STATUS_ORDER, a => (a as ClientSummaryAppointmentDto).status, appointments),
    [appointments]
  );
  const committeeTabs = useMemo(
    () => buildTabs(COMMITTEE_STATUS_ORDER, c => (c as ClientSummaryCommitteeDto).decisionStatus, committees),
    [committees]
  );

  const visibleAppointments =
    apptTab === 'all' ? appointments : appointments.filter(a => a.status === apptTab);
  const visibleCommittees =
    committeeTab === 'all' ? committees : committees.filter(c => c.decisionStatus === committeeTab);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`נתוני לקוח: ${lead.fullName}`} width="560px">
      {loading ? (
        <StateBox>טוען נתוני לקוח...</StateBox>
      ) : error ? (
        <StateBox>
          <AlertTriangle size={28} />
          <div>{error}</div>
        </StateBox>
      ) : summary ? (
        <>
          {/* מסמכים */}
          <Section>
            <SectionHeader>
              <FileText size={18} />
              מסמכים
              <CountBadge>{summary.documentCount}</CountBadge>
            </SectionHeader>
            {summary.documentCount === 0 && <EmptyText>אין מסמכים ללקוח</EmptyText>}
          </Section>

          {/* פגישות */}
          <Section>
            <SectionHeader>
              <Calendar size={18} />
              פגישות
              <CountBadge>{appointments.length}</CountBadge>
            </SectionHeader>
            {appointments.length === 0 ? (
              <EmptyText>אין פגישות</EmptyText>
            ) : (
              <>
                <TabBar tabs={apptTabs} active={apptTab} onChange={setApptTab} />
                <ItemList>
                  {visibleAppointments.map((appt, idx) => (
                    <ItemCard key={idx}>
                      <ItemRow>
                        <ItemTitle>{formatDateTime(appt.appointmentDateTime)}</ItemTitle>
                        <ItemMeta>{appt.statusName}</ItemMeta>
                      </ItemRow>
                      {appt.doctorName && <ItemMeta>{appt.doctorName}</ItemMeta>}
                    </ItemCard>
                  ))}
                </ItemList>
              </>
            )}
          </Section>

          {/* ועדות */}
          <Section>
            <SectionHeader>
              <Gavel size={18} />
              ועדות
              <CountBadge>{committees.length}</CountBadge>
            </SectionHeader>
            {committees.length === 0 ? (
              <EmptyText>אין ועדות</EmptyText>
            ) : (
              <>
                <TabBar tabs={committeeTabs} active={committeeTab} onChange={setCommitteeTab} />
                <ItemList>
                  {visibleCommittees.map((committee, idx) => (
                    <ItemCard key={idx}>
                      <ItemRow>
                        <ItemTitle>{formatDate(committee.committeeDate)}</ItemTitle>
                        {committee.isClosed ? (
                          <DecisionTag>{committee.decisionStatusName}</DecisionTag>
                        ) : (
                          <DecisionTag $muted>ממתין להחלטה</DecisionTag>
                        )}
                      </ItemRow>
                      {committee.isClosed && committee.disabilityPercentage != null && (
                        <ItemMeta>אחוז נכות: {committee.disabilityPercentage}%</ItemMeta>
                      )}
                      {committee.isClosed && committee.decisionNotes && (
                        <DecisionNotes>{committee.decisionNotes}</DecisionNotes>
                      )}
                    </ItemCard>
                  ))}
                </ItemList>
              </>
            )}
          </Section>
        </>
      ) : null}
    </Modal>
  );
};

export default ClientDataViewerModal;
